/* ==========================================================================
   FileUploadField Component
   Task F34: Drag-and-drop file upload for intake form
   ========================================================================== */

import { useState, useRef, useCallback } from 'react'
import { Upload, X, FileText, AlertCircle } from 'lucide-react'
import { Spinner } from '@/components/ui/Spinner'
import { uploadIntakeFile } from '@/api/intake'

type UploadFn = (file: File) => Promise<{ file_url: string; file_name: string }>

interface FileUploadFieldProps {
  token?: string
  fileType?: 'logo' | 'formulary'
  label: string
  accept: string
  maxSizeMB: number
  value: string | null
  fileName: string | null
  onChange: (fileUrl: string | null, fileName: string | null) => void
  onUpload?: UploadFn
  error?: string
  helperText?: string
}

export function FileUploadField({
  token,
  fileType,
  label,
  accept,
  maxSizeMB,
  value,
  fileName,
  onChange,
  onUpload,
  error,
  helperText,
}: FileUploadFieldProps) {
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleFile = useCallback(
    async (file: File) => {
      setUploadError(null)

      // Client-side size validation
      if (file.size > maxSizeMB * 1024 * 1024) {
        setUploadError(`File is too large. Maximum size is ${maxSizeMB}MB.`)
        return
      }

      setUploading(true)
      try {
        let result: { file_url: string; file_name: string }
        if (onUpload) {
          result = await onUpload(file)
        } else if (token && fileType) {
          result = await uploadIntakeFile(token, file, fileType)
        } else {
          throw new Error('No upload handler configured')
        }
        onChange(result.file_url, result.file_name)
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'Upload failed. Please try again.'
        // Try to extract API error message
        if (typeof err === 'object' && err !== null && 'response' in err) {
          const axiosErr = err as { response?: { data?: { detail?: string } } }
          if (axiosErr.response?.data?.detail) {
            setUploadError(axiosErr.response.data.detail)
            return
          }
        }
        setUploadError(message)
      } finally {
        setUploading(false)
      }
    },
    [token, fileType, maxSizeMB, onChange, onUpload],
  )

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleFile(file)
    // Reset input so same file can be re-selected
    if (inputRef.current) inputRef.current.value = ''
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files?.[0]
    if (file) handleFile(file)
  }

  const handleRemove = () => {
    onChange(null, null)
    setUploadError(null)
  }

  const displayError = error || uploadError

  // Uploaded state
  if (value && fileName) {
    return (
      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium text-gray-700">{label}</label>
        <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-md">
          <FileText className="h-5 w-5 text-green-600 flex-shrink-0" />
          <span className="text-sm text-charcoal truncate flex-1">{fileName}</span>
          <button
            type="button"
            onClick={handleRemove}
            className="p-1 rounded hover:bg-green-100 transition-colors"
            aria-label="Remove file"
          >
            <X className="h-4 w-4 text-gray-500" />
          </button>
        </div>
      </div>
    )
  }

  // Upload zone
  return (
    <div className="flex flex-col gap-1">
      <label className="text-sm font-medium text-gray-700">{label}</label>
      <div
        className={`
          relative border-2 border-dashed rounded-md p-6
          transition-colors cursor-pointer
          ${dragOver ? 'border-deep-purple bg-soft-lavender/30' : 'border-gray-300 hover:border-gray-400'}
          ${displayError ? 'border-error' : ''}
          ${uploading ? 'pointer-events-none opacity-60' : ''}
        `}
        onClick={() => !uploading && inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault()
          setDragOver(true)
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={handleInputChange}
          className="hidden"
          aria-label={label}
        />

        <div className="flex flex-col items-center gap-2 text-center">
          {uploading ? (
            <>
              <Spinner size="md" />
              <p className="text-sm text-gray-500">Uploading...</p>
            </>
          ) : (
            <>
              <Upload className="h-8 w-8 text-gray-400" />
              <p className="text-sm text-gray-600">
                <span className="text-deep-purple font-medium">Click to upload</span>
                {' '}or drag and drop
              </p>
              <p className="text-xs text-gray-500">
                Max {maxSizeMB}MB
              </p>
            </>
          )}
        </div>
      </div>

      {displayError && (
        <p className="text-sm text-error flex items-center gap-1" role="alert">
          <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
          {displayError}
        </p>
      )}
      {helperText && !displayError && (
        <p className="text-sm text-gray-600">{helperText}</p>
      )}
    </div>
  )
}

export default FileUploadField
